import { EMPTY_GUID } from '@/constant/constants';
import { regexUUID } from '@/constant/regexs';
import { AllSubKeysMessageResource } from '@/i18n/messages';
import { DepartmentResponse } from '@/server/api/v1/department/department.routes';
import { EmployeeSearchAdvanceResponse } from '@/server/api/v1/employee/type';
import { DossierStatus } from '@/types/enum';
// eslint-disable-next-line no-restricted-imports
import { clsx, type ClassValue } from 'clsx';
import { format, parse } from 'date-fns';
// eslint-disable-next-line no-restricted-imports
import { twMerge } from 'tailwind-merge';
import { getLocalStorage, LocalStorageKeys } from './local-storage';
import { randomNumber } from './random';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Determines whether the environment is development.
 * @returns boolean Whether the environment is dev.
 */
export const isDevEnv = () => {
    return process.env.NODE_ENV === 'development';
};

export function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Trả về Promise đợi một khoảng thời gian ngẫu nhiên (ms) trong đoạn [min, max].
 *
 * Mục đích: dùng trong vòng lặp pagination / batch để tạo nhịp gọi API không đều,
 * tránh bị server nhận diện là spam hoặc bot. Mặc định 0–3000 ms.
 *
 * Ví dụ:
 *   await randomDelay();          // chờ 0–3 giây
 *   await randomDelay(500, 1500); // chờ 0.5–1.5 giây
 */
export function randomDelay(min: number = 0, max: number = 3000) {
    const lo = Math.max(0, Math.min(min, max));
    const hi = Math.max(0, Math.max(min, max));
    const ms = Math.floor(Math.random() * (hi - lo + 1)) + lo;
    return sleep(ms);
}

export function capitalize(txt: string) {
    return txt.charAt(0).toUpperCase() + txt.slice(1);
}

export function capitalizeObjectKeys<T extends Record<string, any>>(
    obj: T
): Record<string, any> {
    const result: Record<string, any> = {};

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const capitalizedKey = capitalize(key);
            result[capitalizedKey] = obj[key];
        }
    }

    return result;
}

export const nameof = <T>(name: Extract<keyof T, string>): string => name;

export type TypeName<T> = keyof { [K in keyof T]: T[K] };
export function nameofType<T>(_?: T): TypeName<T> {
    return undefined as any;
}

export const getDateFormatString = (locale: string, getTime?: boolean) => {
    const timeType = getTime ? 'numeric' : undefined;
    const formatObj = new Intl.DateTimeFormat(locale, {
        hour: timeType,
        minute: timeType,
        second: timeType,
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    }).formatToParts(Date.now());

    return formatObj
        .map((obj) => {
            switch (obj.type) {
                case 'hour':
                    return 'HH';
                case 'minute':
                    return 'MM';
                case 'second':
                    return 'SS';
                case 'day':
                    return 'dd';
                case 'month':
                    return 'MM';
                case 'year':
                    return 'yyyy';
                default:
                    return obj.value;
            }
        })
        .join('');
};

export function mapFirstLetterToLower(input: any): any {
    if (typeof input !== 'object') return input;
    if (Array.isArray(input)) return input.map(mapFirstLetterToLower);
    return Object.keys(input).reduce(function (newObj: any, key) {
        let val = input[key];
        let newVal =
            typeof val === 'object' && val !== null
                ? Array.isArray(val)
                    ? val.map(mapFirstLetterToLower)
                    : mapFirstLetterToLower(val)
                : val;
        newObj[key.charAt(0).toLocaleLowerCase() + key.slice(1)] = newVal;
        return newObj;
    }, {});
}

export function mapFirstLetterToUpper(input: any): any {
    if (typeof input !== 'object') {
        return input;
    }
    if (Array.isArray(input)) return input.map(mapFirstLetterToUpper);
    return Object.keys(input).reduce(function (newObj: any, key) {
        let val = input[key];
        let newVal =
            typeof val === 'object' && val !== null
                ? Array.isArray(val)
                    ? val.map(mapFirstLetterToUpper)
                    : mapFirstLetterToUpper(val)
                : val;
        newObj[key.charAt(0).toUpperCase() + key.slice(1)] = newVal;
        return newObj;
    }, {});
}

/**
 * ### Helper function to append a query parameter to the URL.
 *
 * If URL contains other query parameters besides specified ones in the function,
 * the `addQueryParamIfMissed` function will handle them. This function works
 * by parsing the entire URL and manipulating its query parameters
 * specifically, without altering other parts of the URL.
 *
 * @param url - the URL to append the query parameter to.
 * @param parameter - the query parameter to append.
 * @param value - the value of the query parameter.
 * @returns the URL with the query parameter appended.
 *
 * @see https://stackoverflow.com/q/5999118
 */
export function addQueryParamIfMissed(
    url: string,
    parameter: string | undefined | null,
    value: string
): string {
    const urlObject = new URL(url);
    if (parameter && !urlObject.searchParams.has(parameter)) {
        urlObject.searchParams.append(parameter, value);
    }
    return urlObject.toString();
}

export function truncate(str: string, length: number) {
    return str.length > length ? `${str.substring(0, length)}...` : str;
}

export const numberToPercent = (number: number) => {
    const percent = new Intl.NumberFormat('en-US', {
        style: 'percent',
        maximumSignificantDigits: 2,
    });

    return percent.format(number / 100);
};
export function isNotNullOrUndefined(val: any) {
    return val !== null && val !== undefined;
}

export function isNotNullOrEmpty(item: string | any[]) {
    return item?.length > 0;
}

export const numberInputOnly = (
    event: React.KeyboardEvent<HTMLInputElement>
) => {
    ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();
};

const arrAllowKeys = [
    'Delete',
    'Backspace',
    'ArrowLeft',
    'ArrowRight',
    'Ctrl',
    'Tab',
];

export const allowKeysOnly = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const selectAll = (key === 'a' || key === 'A') && e.ctrlKey; // tổ hợp phím chọn hết
    const copy = (key === 'c' || key === 'C') && e.ctrlKey; // tổ hợp phím copy
    const paste = (key === 'v' || key === 'V') && e.ctrlKey; // tổ hợp phím paste
    if (!arrAllowKeys.includes(key) && !selectAll && !copy && !paste) {
        e.preventDefault();
    }
};

export const taxCodeInput = (value: string) => {
    const cleanedStr = value.replace(/-/g, '');
    const result = cleanedStr
        ?.replace(/[^\dA-Z]/g, '')
        .replace(/(.{10})/g, '$1-');
    return result.endsWith('-') ? result.slice(0, -1) : result;
};

export const numberOnly = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const selectAll = (key === 'a' || key === 'A') && e.ctrlKey; // tổ hợp phím chọn hết
    const copy = (key === 'c' || key === 'C') && e.ctrlKey; // tổ hợp phím copy
    const paste = (key === 'v' || key === 'V') && e.ctrlKey; // tổ hợp phím paste
    if (
        !key.match(/^\d+$/) &&
        !arrAllowKeys.includes(key) &&
        !selectAll &&
        !copy &&
        !paste
    ) {
        e.preventDefault();
    }
};

// Hàm tạo formData
export function createFormData(
    data: any,
    formData = new FormData(),
    parentKey?: string
) {
    switch (true) {
        case data === undefined:
        case data === null:
            break;
        case typeof data === 'object' &&
            !Array.isArray(data) &&
            !(data instanceof Date) &&
            !(data instanceof File):
            Object.keys(data).forEach((key) => {
                formData = createFormData(
                    data[key],
                    formData,
                    parentKey ? `${parentKey}.${key}` : key
                );
            });
            break;
        case Array.isArray(data) && !(data instanceof File):
            if (data.length > 0) {
                const isArrFile = data.every((x) => x instanceof File);
                if (isArrFile && data.length > 1) {
                    for (let i = 0; i < data.length; i++) {
                        i === 0 &&
                            formData.append(
                                `${parentKey}._${randomNumber()}`,
                                ''
                            );

                        formData.append(`${parentKey ?? ''}`, data[i] as any);
                    }
                } else {
                    for (let i = 0; i < data.length; i++) {
                        if (data[i] instanceof File) {
                            i === 0 &&
                                formData.append(
                                    `${parentKey}._${randomNumber()}`,
                                    ''
                                );

                            formData.append(
                                `${parentKey ?? ''}[${i}]`,
                                data[i] as any
                            );
                        } else
                            formData = createFormData(
                                data[i],
                                formData,
                                parentKey ? `${parentKey}[${i}]` : `${i}`
                            );
                    }
                }
            } else {
                // formData.append(`${parentKey}[${0}]`, ''); //TODO: Fix lỗi khi gửi mảng rỗng
            }
            break;
        case Number(data) === data && data % 1 !== 0:
            formData.append(parentKey ?? '', data.toString().replace('.', ','));
            break;
        case data instanceof File:
            formData.append(`${parentKey}._${randomNumber()}`, '');
            formData.append(parentKey ?? '', data);
            break;
        default:
            formData.append(parentKey ?? '', data);
            break;
    }

    return formData;
}

/**
 * Hàm xóa các Property có '_' ở đầu
 * @param {*} arg
 * @returns
 */
export function RemovePropertiesWithUnderscoreAtFirst<T = any>(arg: any): T {
    /**
     * File, string, number pass...
     */
    if (!arg || typeof arg !== 'object' || arg instanceof File) return arg;
    let result: any = {};

    if (Array.isArray(arg)) {
        result = [];
        arg.forEach((x) => {
            const obj = RemovePropertiesWithUnderscoreAtFirst(x);
            result.push(obj);
        });
    } else {
        Object.keys(arg).forEach((key) => {
            if (!key.startsWith('_')) {
                const newChild = RemovePropertiesWithUnderscoreAtFirst(
                    arg[key]
                );
                result[key] = newChild;
            }
        });
    }
    return result as T;
}

/**Băt error và xuất bản dịch lỗi ra */
export function getErrorMsgTranslation(
    args?: string | string[]
): AllSubKeysMessageResource {
    if (!args) return 'System_AnErrorHasOccurred';
    if (typeof args === 'string') {
        switch (args) {
            case '':
                return 'System_AnErrorHasOccurred';
        }
    }
    if (Array.isArray(args)) {
    }

    return 'System_AnErrorHasOccurred';
}

/**Thay thế toàn bộ giá trị underfined có trong tham số thành null, và createFormData nếu cần */
export function replaceUndefinedWithNull<T = any>(
    input: any,
    isCreateFormData?: boolean
): T {
    let hvFile = false;
    isCreateFormData ??= true;

    const func = (input: any): any => {
        if (Array.isArray(input)) {
            // Nếu là mảng, duyệt qua từng phần tử
            return input.length ? input.map((item) => func(item)) : [];
        } else if (typeof input === 'object' && input !== null) {
            if (input instanceof File || input instanceof Blob) {
                hvFile = true;
                return input;
            }
            // Nếu là object, duyệt qua từng thuộc tính
            return Object.keys(input).reduce(
                (acc, key) => {
                    acc[key] = func(input[key]);
                    return acc;
                },
                {} as Record<string, any>
            );
        } else if (input === undefined) {
            // Nếu là undefined, thay thế bằng null
            return null;
        } else {
            // Các trường hợp khác trả về giá trị gốc
            return input;
        }
    };

    const res = func(input);
    const formData = isCreateFormData && hvFile ? createFormData(res) : res;
    return formData as T;
}

/**Hàm này lấy ra các nhãn ngôn ngữ của trạng thái hồ sơ */
export function getDossierStatusResourceCode(
    dossierStatus?: DossierStatus
): AllSubKeysMessageResource<'DossierManagement'> {
    switch (dossierStatus) {
        case DossierStatus.WaittingReceive:
            return 'DossierStatus_WaittingReceive';
        case DossierStatus.Processing:
            return 'DossierStatus_Processing';
        case DossierStatus.NotResolve:
            return 'DossierStatus_NotResolve';
        case DossierStatus.ReceivedWaitingTransfer:
            return 'DossierStatus_ReceivedWaitingTransfer';
        case DossierStatus.WaitingForReturn:
            return 'DossierStatus_WaitingForReturn';
        case DossierStatus.Returned:
            return 'DossierStatus_Returned';
        case DossierStatus.RewriteRequirement:
            return 'DossierStatus_RewriteRequirement';
        case DossierStatus.Draft:
        default:
            return 'DossierStatus_Draft';
    }
}

/**Gen text thông tin nhân viên nhận, gửi xử lý */
export function genProccessStaffInfo(
    emp?: EmployeeSearchAdvanceResponse,
    showUnitInfo?: boolean
) {
    if (!emp) return 'Nhân viên xử lý';
    return `${emp.FullName}${emp.Department ? ` / ${emp.Department.Name}` : ''}${showUnitInfo && emp.Unit ? ` / ${emp.Unit.Name}` : ''}`;
}

/**Gen text thông tin đơn vị */
export function genUnitInfo(unit?: DepartmentResponse, showDefCode?: boolean) {
    if (!unit) return '';
    showDefCode ??= true;
    return `${unit.Name}${unit.Origin_Def_Code && showDefCode ? ` (Mã đơn vị: ${unit.Origin_Def_Code})` : ''}`;
}

/**Hàm format datetime theo 1 format truyền vào - Sử dụng lib date-fns */
export function formatDateTime(
    strDate?: string | Date | null,
    formatStr?: string
) {
    if (!strDate) return '';
    formatStr ??= 'dd/MM/yyyy hh:mm a';

    let dateStr = format(strDate, formatStr);
    const _locale = getLocalStorage(LocalStorageKeys.Locale);

    if (!_locale || _locale === 'vi') {
        dateStr = dateStr.replace('AM', 'SA').replace('PM', 'CH');
    }

    return dateStr;
}

/**Hàm convert ISO string ==> Date  */
export function convertISOStringToDate(str?: string | null): Date | undefined {
    if (!str) return;
    return new Date(str);
}

export const randomuuid = () =>
    `${new Date().getTime().toString()}_${Math.random().toString(36).substring(2, 9)}`;

/**Format tiên VNĐ */
export function formatCurrencyVND(amount?: number) {
    amount ??= 0;
    return new Intl.NumberFormat('vi-VN', {
        style: 'decimal',
        currency: 'VND',
    }).format(amount);
}

/**Kiểm tra GUID rỗng */
export function isEmptyUUID(uuid?: any) {
    return typeof uuid === 'string' && uuid === EMPTY_GUID;
}

/**Xóa đi các id không hợp lệ -_- */
export function removeInvalidUUID(uuid: string[]) {
    const validUUID = uuid.filter((x) => {
        const isEmpty = isEmptyUUID(x);
        const isValidUUID = regexUUID.test(x);
        return !isEmpty && isValidUUID;
    });

    // console.log({ uuid, result });
    return validUUID;
}

/**
 * Thực hiện covert các chuỗi id có dạng "'id1','id2'" thành mảng ['id1', 'id2']
 * @param str
 * @returns Array
 */
export function convertWrapStrToArray(str?: string) {
    if (!str || str === '') return [];
    return str
        .split(',')
        .map((x) => x.trim().replace(/^'|'$/g, ''))
        .filter((x) => x);
}

/**
 * Hàm wrap các chuỗi thành dạng 'chuỗi1','chuỗi2'
 * @param targets
 * @returns
 */
export function wrapQuote(targets?: string | string[]) {
    if (!targets || targets.length === 0) return undefined;
    if (typeof targets === 'string') return `'${targets}'`;
    return targets.map((x) => `'${x}'`).join(',');
}

/**
 * Xác định kiểu của object
 * @param obj
 * @param keys
 * @returns
 */
export function isType<T extends object>(
    obj: unknown,
    keys: (keyof T)[] = []
): obj is T {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        keys.every((key) => key in (obj as T))
    );
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param html - The HTML content to sanitize
 * @returns The sanitized HTML content
 */
export function sanitizeHtml(html?: string) {
    if (!html || typeof html !== 'string') return '';

    // 1. Xóa các thẻ script và style để tránh XSS
    html = html.replace(/<(script|style)[^>]*>.*?<\/\1>/gis, '');

    // 2. Danh sách thẻ được phép
    const allowedTags = [
        'p',
        'strong',
        'em',
        'ul',
        'ol',
        'li',
        'a',
        'br',
        'img',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'code',
        'pre',
        'table',
        'thead',
        'tbody',
        'tr',
        'td',
        'th',
    ];
    const allowedAttributes = [
        'href',
        'title',
        'src',
        'alt',
        'class',
        'style',
        'rel',
        'target',
        'width',
    ]; // Mảng chung cho tất cả các thẻ

    // 3. Xóa các thẻ không hợp lệ (chỉ giữ thẻ trong danh sách whitelist)
    const allowedTagsRegex = new RegExp(
        `<(?!\/?(?:${allowedTags.join('|')})(\\s|>))[^>]+>`,
        'gi'
    );
    html = html.replace(allowedTagsRegex, '');

    // 4. Xóa các thuộc tính không hợp lệ
    html = html.replace(/<(\w+)([^>]*)>/g, (match, tag, attributes) => {
        if (!allowedTags.includes(tag)) return match;

        // Lọc các thuộc tính hợp lệ
        const validAttrs = attributes
            .split(/\s+/)
            .filter((attr: string) => {
                const attrName = attr.split('=')[0];
                return allowedAttributes.includes(attrName);
            })
            .join(' ');

        return validAttrs ? `<${tag} ${validAttrs}>` : `<${tag}>`;
    });

    // 5. Xóa các thẻ rỗng
    html = html.replace(/<(\w+)\s*>\s*<\/\1>/g, '');

    // 6. Xóa khoảng trắng thừa giữa các thẻ
    html = html.replace(/\s+/g, ' ').trim();

    return html;
}

/**
 * Chuyển đổi phút thành giờ hoặc ngày
 * @param min - Số phút cần chuyển đổi
 * @default 0
 * @param type - Loại chuyển đổi ('hours' hoặc 'days')
 * @default 'hours'
 * @returns Số giờ hoặc số ngày tương ứng
 */
export function convertMinutesTo(min?: number) {
    min ??= 0;
    let days = 0;
    let hours = 0;
    let minutes = 0;

    if (min) {
        days = min / 1440;
        days = parseFloat(days.toFixed(2));
        hours = min / 60;
        hours = parseFloat(hours.toFixed(2));
        minutes = minutes;
    }

    return { days, hours, minutes };
}

/**
 * Chuyển đổi ngày hoặc giờ thành phút
 * @param type - Loại chuyển đổi ('days' hoặc 'hours')
 * @param value - Số ngày hoặc số giờ cần chuyển đổi
 * @returns Số phút tương ứng
 */
export function convertToMinutes(value?: number, type?: 'days' | 'hours') {
    type ??= 'days';
    if (!value) return 0;
    if (type === 'days') return value * 1440; // 1 ngày = 1440 phút chưa tính giờ làm việc hành chính
    if (type === 'hours') return value * 60; // 1 giờ = 60 phút chưa tính giờ làm việc hành chính
    return 0;
}

// Hàm generic để kiểm tra enum bất kỳ
export function isEnumValue<E>(
    enumObj: E,
    value: unknown
): value is E[keyof E] {
    return Object.values(enumObj as any).includes(value as any);
}

export function convertToDate(str?: string) {
    try {
        if (!str) return undefined;

        let date = new Date(str);
        if (!isNaN(date.getTime())) {
            return date;
        } else {
            date = parse(str, 'dd/MM/yyyy', new Date());
        }

        return date;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}

export function createSlug(title: string) {
    return title
        .replace(/Đ/g, 'D') // thay Đ -> D
        .replace(/đ/g, 'd') // thay đ -> d
        .normalize('NFD') // tách dấu
        .replace(/[\u0300-\u036f]/g, '') // xóa dấu
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s_]/g, '') // loại bỏ ký tự đặc biệt
        .replace(/\s+/g, '_') // thay khoảng trắng bằng _
        .replace(/_+/g, '_'); // gom nhiều dấu _ thành 1
}

export function sortByNumberNullable<T>(
    array: T[],
    key: keyof T,
    order: 'asc' | 'desc' = 'asc',
    /**null để đầu hay cuối? */
    nullPosition: 'first' | 'last' = 'first'
): T[] {
    const multiplier = order === 'asc' ? 1 : -1;

    return [...array].sort((a, b) => {
        const valueA = a[key] as number | null | undefined;
        const valueB = b[key] as number | null | undefined;

        // Xử lý null/undefined
        if (valueA == null && valueB == null) return 0;
        if (valueA == null) return nullPosition === 'last' ? 1 : -1;
        if (valueB == null) return nullPosition === 'last' ? -1 : 1;

        // Cả hai đều có giá trị number
        return (valueA - valueB) * multiplier;
    });
}

/**
 * Chuyển một chuỗi key sang camelCase.
 * Hỗ trợ: snake_case, SCREAMING_SNAKE, kebab-case, PascalCase.
 *
 * @example
 * toCamelCaseKey('access_token')   // 'accessToken'
 * toCamelCaseKey('AccessToken')    // 'accessToken'
 * toCamelCaseKey('EXPIRES_IN')     // 'expiresIn'
 * toCamelCaseKey('kebab-case')     // 'kebabCase'
 */
export function toCamelCaseKey(str: string): string {
    if (/_|-/.test(str)) {
        // snake_case / SCREAMING_SNAKE / kebab-case → camelCase
        return str
            .toLowerCase()
            .replace(/[-_](.)/g, (_, char: string) => char.toUpperCase());
    }
    // PascalCase → camelCase (chỉ lowercase chữ cái đầu)
    return str.charAt(0).toLowerCase() + str.slice(1);
}

/**
 * Đệ quy chuyển toàn bộ key của object (ở mọi cấp độ lồng nhau) sang camelCase.
 * An toàn với unknown — trả về cùng kiểu cấu trúc nhưng key đã được chuẩn hóa.
 *
 * @example
 * deepCamelCase({ AccessToken: '...', ExpiresIn: 300 })
 * // → { accessToken: '...', expiresIn: 300 }
 *
 * deepCamelCase([{ user_id: 1 }, { user_id: 2 }])
 * // → [{ userId: 1 }, { userId: 2 }]
 */
export function deepCamelCase(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(deepCamelCase);
    }

    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([k, v]) => [
                toCamelCaseKey(k),
                deepCamelCase(v),
            ])
        );
    }

    return value;
}
